import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCheck, FaTimes } from "react-icons/fa";
import { Link } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  CardRoot,
  Heading,
  HStack,
  notify,
  Text,
  VStack,
} from "../../../../components/ui";
import {
  Suggestion,
  suggestionService,
} from "../../../../services/suggestionService";
import { useSuggestions } from "../../../../contexts/SuggestionsContext";

interface SuggestedShortcutsProps {
  /** Called after a suggestion is accepted so the shortcut list below picks up the new row. */
  onAccepted: () => void;
}

/** Only the strongest few — this block sits above the list the page is actually for. */
const TOP_ROWS = 5;

/**
 * The top pending shortcut suggestions, shown in context above the tenant's real shortcuts, in the
 * same one-line-per-row layout as the Suggestions page. Renders nothing when there is nothing to
 * decide. The rest — and the full history, including dismissed ones — is on the Suggestions page.
 */
const SuggestedShortcuts: React.FC<SuggestedShortcutsProps> = ({
  onAccepted,
}) => {
  const { t } = useTranslation();
  const { refreshCounts } = useSuggestions();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setSuggestions(await suggestionService.list("Shortcut", "Pending"));
    } catch {
      // Non-critical surface — the Suggestions page is the authoritative one, so a failure here
      // stays quiet rather than firing a toast over the shortcut list.
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAccept = async (suggestion: Suggestion) => {
    setBusyId(suggestion.id);
    try {
      await suggestionService.accept(suggestion.id);
      notify({
        type: "success",
        title: t("app.suggestions.shortcuts.acceptedToast"),
      });
      await load();
      refreshCounts();
      onAccepted();
    } catch (error: any) {
      notify({
        type: "error",
        title: t("app.suggestions.errors.acceptFailed"),
        description: error?.response?.data?.message ?? error?.message,
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleDismiss = async (suggestion: Suggestion) => {
    setBusyId(suggestion.id);
    try {
      await suggestionService.dismiss(suggestion.id);
      notify({
        type: "success",
        title: t("app.suggestions.shortcuts.dismissedToast"),
      });
      await load();
      refreshCounts();
    } catch (error: any) {
      notify({
        type: "error",
        title: t("app.suggestions.errors.dismissFailed"),
        description: error?.response?.data?.message ?? error?.message,
      });
    } finally {
      setBusyId(null);
    }
  };

  if (suggestions.length === 0) return null;

  // The API orders by need, not strength; rank here so the five shown are the ones worth acting on.
  const top = [...suggestions]
    .sort((a, b) => b.score - a.score)
    .slice(0, TOP_ROWS);

  return (
    <VStack align="stretch" gap={2}>
      <Box>
        <Heading size="md">
          {t("app.promptShortcuts.suggested.heading")} ({suggestions.length})
        </Heading>
        <Text fontSize="sm" color="var(--text-secondary-color)" mt={1}>
          {t("app.promptShortcuts.suggested.description")}{" "}
          <Link
            to="/admin/suggestions"
            style={{ textDecoration: "underline", fontWeight: 600 }}
          >
            {t("app.promptShortcuts.suggested.viewAllCta")}
          </Link>
        </Text>
      </Box>

      {/* Same card surface as the Suggestions page and the dashboard's KPI tiles. */}
      <CardRoot overflow="hidden">
        {top.map((s, i) => (
          <HStack
            key={s.id}
            gap={3}
            px={3}
            py={1.5}
            align="center"
            borderTopWidth={i > 0 ? "1px" : 0}
            borderColor="var(--card-border)"
          >
            <Badge
              colorPalette={s.level === "CompanyWide" ? "blue" : "purple"}
              size="sm"
              flexShrink={0}
              maxW={{ base: "110px", xl: "160px" }}
              truncate
              display="block"
            >
              {s.level === "CompanyWide"
                ? t("app.suggestions.shortcuts.scopeAllCustomers")
                : (s.customerCompanyName ??
                  t("app.suggestions.shortcuts.scopeCustomer"))}
            </Badge>
            {/* Title keeps its width; the prompt body only gets the leftover and yields first. */}
            <HStack gap={2} flex="1" minW={0} align="baseline">
              <Text
                fontSize="sm"
                fontWeight={500}
                flex="0 1 auto"
                minW="6em"
                truncate
              >
                {s.title}
              </Text>
              <Text
                fontSize="xs"
                fontFamily="mono"
                color="var(--text-secondary-color)"
                flex="1 1 0"
                minW={0}
                truncate
                display={{ base: "none", md: "block" }}
              >
                {s.promptBody}
              </Text>
            </HStack>
            <Text
              fontSize="xs"
              color="var(--text-secondary-color)"
              whiteSpace="nowrap"
              flexShrink={0}
              display={{ base: "none", xl: "block" }}
            >
              {t("app.suggestions.shortcuts.evidenceCounts", {
                sessions: s.occurrenceCount,
                customers: s.distinctCustomerCount,
              })}
            </Text>
            <HStack gap={1} flexShrink={0}>
              <Button
                size="xs"
                variant="outline"
                colorPalette="blue"
                color="colorPalette.fg"
                borderColor="colorPalette.solid"
                _hover={{
                  bg: "colorPalette.subtle",
                  borderColor: "colorPalette.solid",
                }}
                loading={busyId === s.id}
                onClick={() => handleAccept(s)}
              >
                <FaCheck /> {t("app.suggestions.accept")}
              </Button>
              <Button
                size="xs"
                intent="secondary"
                loading={busyId === s.id}
                onClick={() => handleDismiss(s)}
              >
                <FaTimes /> {t("app.suggestions.dismiss")}
              </Button>
            </HStack>
          </HStack>
        ))}
      </CardRoot>
    </VStack>
  );
};

export default SuggestedShortcuts;
